import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import Logger from "../../../services/Logger";
import StatsJigsawService from "../../../services/StatsJigsawService";
import StatsSummaryTile from "../../pzl_common/stats/StatsSummaryTile";

const RANGE_OPTIONS = [
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "1m", label: "Last month", days: 30 },
    { value: "3m", label: "Last 3 months", days: 90 },
    { value: "12m", label: "Last 12 months", days: 365 },
];

const DEFAULT_RANGE = "1m";

const rangeDays = (value) => RANGE_OPTIONS.find((option) => option.value === value)?.days ?? 30;

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatPercent = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
};

const formatDuration = (seconds) => {
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
    if (totalSeconds < 60) return `${Math.round(totalSeconds)} sec`;
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = Math.round(totalSeconds % 60);
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
};

export default function JigsawPlayerStatsDialog({ open, onClose, player, language }) {
    const [range, setRange] = useState(DEFAULT_RANGE);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    const cloudUserId = player?.cloudUserId || null;
    const sessionId = player?.sessionId || null;
    const title = player?.name || player?.email || "Player";

    useEffect(() => {
        if (!open || (!cloudUserId && !sessionId)) {
            setStats(null);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const end = dayjs();
                const start = end.subtract(rangeDays(range), "day").startOf("day");
                const result = await StatsJigsawService.playerPlayStats({
                    cloudUserId,
                    sessionId: cloudUserId ? undefined : sessionId,
                    start: start.unix(),
                    end: end.unix(),
                    language,
                });
                if (!cancelled) {
                    setStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load jigsaw player stats:", err);
                if (!cancelled) {
                    setError("Failed to load this player's statistics.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, cloudUserId, sessionId, language, range]);

    const totals = stats?.totals ?? {};
    const daily = useMemo(() => (Array.isArray(stats?.daily) ? stats.daily : []), [stats]);
    const byDifficulty = useMemo(
        () => (Array.isArray(stats?.byDifficulty) ? stats.byDifficulty : []),
        [stats],
    );
    const dailyLabels = useMemo(() => daily.map((row) => dayjs(row.date).format("MMM D")), [daily]);
    const hasPlays = Number(totals.plays ?? 0) > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ fontWeight: 600 }}>
                {title}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        {player?.type === "GUEST" ? "Guest session" : player?.email || "Signed-in player"}
                        {player?.score != null ? ` · score ${formatNumber(player.score)}` : ""}
                    </Typography>
                    {player?.type === "SIGNED" && player?.notificationsEnabled != null ? (
                        <Chip
                            size="small"
                            color={player.notificationsEnabled ? "success" : "default"}
                            label={player.notificationsEnabled ? "Notification On" : "Notification Off"}
                        />
                    ) : null}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="jigsaw-player-stats-range-label">Duration</InputLabel>
                        <Select
                            labelId="jigsaw-player-stats-range-label"
                            label="Duration"
                            value={range}
                            onChange={(event) => setRange(event.target.value)}
                        >
                            {RANGE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                        {language} · plays in this window
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                gap: 1,
                                mb: 3,
                            }}
                        >
                            <StatsSummaryTile label="Plays" value={formatNumber(totals.plays)} hint="Started in this period" />
                            <StatsSummaryTile
                                label="Completed"
                                value={formatNumber(totals.completedPlays)}
                                hint={formatPercent(totals.completionRate)}
                            />
                            <StatsSummaryTile
                                label="Average solve time"
                                value={formatDuration(totals.averageDurationSeconds)}
                                hint={`${formatNumber(totals.timedCompletedPlays)} timed`}
                            />
                            <StatsSummaryTile
                                label="Average attempts"
                                value={
                                    Number.isFinite(Number(totals.averageAttempts))
                                        ? Number(totals.averageAttempts).toFixed(1)
                                        : "—"
                                }
                                hint="Times a board was opened"
                            />
                        </Box>

                        {!hasPlays ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No jigsaw plays for this player in this period.
                            </Alert>
                        ) : null}

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Plays per day
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    How often this player started and finished jigsaws, same daily series as the Users
                                    tab (plays instead of distinct players).
                                </Typography>
                                {daily.length > 0 ? (
                                    <LineChart
                                        height={280}
                                        xAxis={[{ scaleType: "point", data: dailyLabels }]}
                                        yAxis={[{ min: 0 }]}
                                        series={[
                                            {
                                                data: daily.map((row) => Number(row.plays) || 0),
                                                label: "Plays",
                                                color: "#1976d2",
                                                showMark: daily.length <= 40,
                                            },
                                            {
                                                data: daily.map((row) => Number(row.completedPlays) || 0),
                                                label: "Completed",
                                                color: "#3BA666",
                                                showMark: daily.length <= 40,
                                            },
                                        ]}
                                    />
                                ) : (
                                    <Alert severity="info">No daily activity in this period.</Alert>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Board sizes played
                                </Typography>
                                {byDifficulty.length > 0 ? (
                                    <BarChart
                                        height={280}
                                        xAxis={[{ scaleType: "band", data: byDifficulty.map((row) => row.band) }]}
                                        series={[
                                            {
                                                data: byDifficulty.map((row) => Number(row.plays) || 0),
                                                label: "Plays",
                                                color: "#1976d2",
                                            },
                                            {
                                                data: byDifficulty.map((row) => Number(row.completedPlays) || 0),
                                                label: "Completed",
                                                color: "#3BA666",
                                            },
                                        ]}
                                    />
                                ) : (
                                    <Alert severity="info">No plays to group by board size in this period.</Alert>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
